import * as w from '@packages/systems/wfdl';
import * as a from '@packages/systems/wfdl-ast';
import {noConstraints} from '@packages/systems/constraints-constants';
import type {DefaultBaseStyleConfig} from '@packages/systems/style/types';
import type {
  Constraints,
  NormalizedConstraints,
} from '@packages/systems/constraints-types';
import {
  defaultIntegratedTypes,
  defaultIntegratedFieldValues,
} from '@packages/utilities/integrated-types';
import {
  iconName,
  qualify,
  type Qualified,
  type IconName,
} from '@packages/systems/wfdl-Qualified';

type IntegratedComponentShape = {
  render: {
    id: string;
    type: Qualified<w.Component.ComponentName>;
    data: {
      [key: string]: a.Expression;
    };
  };
  contextType?: w.Type;
  fields?: {
    [key: string]: {
      dataType: w.Type;
      defaultValue: a.Expression;
    };
  };
  constraints?: Constraints;
  displayName?: string;
  icon?: Qualified<IconName>;
  style?: DefaultBaseStyleConfig;
};

const genericIcon: Qualified<IconName> = qualify<IconName>(
  w.Plugins.name('Basic'),
  iconName('DivBlock')
);

export const defaults: {
  constraints: NormalizedConstraints;
  dataType: w.Type;
  contextType: w.Type;
  displayName: string;
  icon: Qualified<IconName>;
  style: any;
} = {
  constraints: noConstraints,
  dataType: a.TEmptyRecord,
  contextType: a.TTypeReference('context'),
  displayName: 'Component',
  icon: genericIcon,
  style: undefined,
};

type IntegratedData = {
  styleBlockIds: a.Expression;
  id: a.Expression;
  xattr: a.Expression;
};

export const defaultIntegratedData: IntegratedData = {
  styleBlockIds: a.EData(['styleBlockIds']),
  id: a.EData(['attr', 'id']),
  xattr: a.EData(['xattr']),
};
/**
 * TODO: We need to refine this API a bit and figure out how we want authors to
 * opt in (or out) of default data https://app.clickup.com/t/xvf3td
 *
 * An `IntegratedComponent` is a kind of "base" WFDL `Component` that comes
 * pre-configured with a set of common fields useful within the Webflow
 * platform.
 *
 * These common fields are:
 *
 * - `styleBlockIds`: Allows customers to apply classes to an element somewhere
 *   inside your component definition.
 */
export function createIntegratedComponent(
  shape: IntegratedComponentShape
): w.Component.ComponentType {
  let fieldDataTypes: {[key: string]: w.Type} | undefined;
  let fieldDefaultValues: {[key: string]: a.Expression} | undefined;
  if (shape.fields) {
    fieldDataTypes = {};
    fieldDefaultValues = {};
    for (const [key, field] of Object.entries(shape.fields)) {
      fieldDataTypes[key] = field.dataType;
      fieldDefaultValues[key] = field.defaultValue;
    }
  }

  return w.Component.create({
    render: a.EElement({
      id: shape.render.id,
      data: a.ERecord({
        ...(defaultIntegratedData as {
          [key: string]: a.Expression;
        }),
        ...shape.render.data,
      }),
      type: shape.render.type,
    }),
    dataType: a.TRecord(
      fieldDataTypes
        ? {
            ...defaultIntegratedTypes,
            ...fieldDataTypes,
          }
        : defaultIntegratedTypes
    ),
    defaultData: a.EFunction({
      param: 'context',
      body: fieldDefaultValues
        ? a.ERecord({
            ...defaultIntegratedFieldValues,
            ...fieldDefaultValues,
          })
        : a.ERecord(defaultIntegratedFieldValues),
    }),
    contextType: shape.contextType || defaults.contextType,
    constraints: {...defaults.constraints, ...shape.constraints},
    displayName: shape.displayName || defaults.displayName,
    icon: shape.icon || defaults.icon,
    style: shape.style || defaults.style,
  });
}

/**
 * Determines whether a given type is "Integrated". Typically used in
 * conjunction with `IntegratedTypes`:
 *
 * ```javascript
 * hasFieldType(component, 'styleBlockIds', IntegratedTypes.styleBlockIds); // true
 * hasFieldType(component, 'random', TRecord({})); // false
 * ```
 */
export function hasFieldType(
  comp: w.Component.ComponentType,
  fieldName: string,
  type: w.Type
): boolean {
  const fields = w.Types.getRecordFields(w.Component.getDataType(comp));

  if (
    fields != null &&
    fields[fieldName] &&
    w.Types.equal(fields[fieldName], type)
  ) {
    return true;
  }

  return false;
}
